import React, { useCallback } from 'react';
import { MdClose } from 'react-icons/md';
import Modal from '~/components/Modal';
import FormFooter from '~/components/form/form-footer';
import { FormSelect } from '~/components/form';
import { useForm } from '~/hooks';
import { OwnerIndividual } from './OwnerIndividual';
import { OwnerEntity } from './OwnerEntity';
import { buyerSellerRelationship } from '~/api';


/**
 * @typedef AddOwnerValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 'Your File Number'
 * @property {object}   ownerPrimary 
 * @property {object}   ownerSecondary 
 * @property {[object]} ownerPrimaryType 
 * @property {[object]} ownerSecondaryType
 * @property {[object]} ownerTypeOptions
 * @property {object}   relationship
 * @property {[object]} relationshipOptions
 * @property {boolean}  addOwnerFlag
 * @property {[object]} data
 * @property {boolean}  editOwner
 * 
 * 
 * @typedef FormState
 * @property {AddOwnerValues} values The current form values
 */

/** @type {AddOwnerValues} */
const defaultState = {
    id: 0,
    sectionId: 0,
    titleOpinionId: 0,
    ownerPrimary: null,
    ownerPrimaryType: { label: '...', value: '' },
    ownerSecondaryType: {label: '...', value: '' },
    ownerSecondary: null,
    ownerTypeOptions: [
        {
            id: 1,
            label: 'Individual',
            value: 'Individual'
        },
        {
            id: 2,
            label: 'Entity',
            value: 'Entity'
        }
    ],
    relationship: { label: '...', value: '' },
    relationshipOptions: [],
    data: [],
    addOwnerModalFLag: false,
    editOwner: false
};

const loadData = async (values, props) => {
    const defaultOwnerType = defaultState.ownerTypeOptions.find(s => s.id == 1);
    const relationshipOptions = (await buyerSellerRelationship.getBuyerSellerRelationships()).map(f => ({
        label: f.Name, value: f.Name
    }));
    let editMode= false;
    if(Object.keys(props.ownerDetailData).length === 0){
        editMode = true;
    }
    
    return {
        ownerDetailData: props.ownerDetailData,
        ...values,
        ...props,
        relationshipOptions: relationshipOptions,
        sectionId: values ? values.sectionId: 0,
        relationship: !editMode ? props.ownerDetailData._data.relationship : relationshipOptions[0],
        ownerPrimaryType: !editMode ? props.ownerDetailData._data.ownerPrimaryType : defaultOwnerType,
        ownerSecondaryType: !editMode ? props.ownerDetailData._data.ownerSecondaryType :  defaultOwnerType
    };
};


const AddOwner= (props) => {

    const editDetailData = props.editOwner ? props.ownerDetailData._data : defaultState;    
    const onLoad = useCallback((values, props) => {
        return loadData(values, props);
    }, [props.isModalOpen]);


    const { state, fieldActions } = useForm({
        initialState: { ...defaultState },
        props,
        onLoad,
        debug: true
    });     

    return (
        <Modal isModalOpen={props.isModalOpen} preventScroll className='w-3/4 bg-clip-border bg-white rounded-md'>
            <section className='h-full flex flex-col'>
                <header className='pt-3 pb-2 px-8 border-b flex items-center justify-between'>
                    <p className='font-semibold'>{props.headerTxt}</p>
                    <button 
                        className='rounded-full p-2 hover:bg-gray-150 focus:outline-none'
                        onClick={props.onCancel}
                    >
                        <MdClose />
                    </button>
                </header>
                <div className='flex-grow overflow-y-scroll'>
                    <div className="px-8 py-4 grid grid-cols-4 grid-flow-row gap-x-2 gap-y-2 text-sm mb-2">
                        <FormSelect
                            label="Owner Type*"
                            type='select'
                            required
                            options={state.values.ownerTypeOptions}
                            isClearable={false}
                            groupClassName="col-span-1"
                            className="w-full border-1 border-gray-200 rounded bg-gray-50"
                            path='ownerPrimaryType'   
                            value={state.values.ownerPrimaryType}
                            error={state.errors.ownerPrimaryType}
                            {...fieldActions}                                        
                            onChange={(ownerPrimaryType) => {
                                fieldActions.onChange(ownerPrimaryType, 'ownerPrimaryType');
                                fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');  
                            }}                             
                        />
                        <div className="col-span-5">
                            {(state.values.ownerPrimaryType.value) == 'Individual' ?
                                <OwnerIndividual 
                                    path='ownerPrimary'
                                    detailData={props.editOwner ? editDetailData.ownerPrimary: null}
                                    onOwnerIndividualChange={(values) => {
                                        fieldActions.onChange(values, 'ownerPrimary');
                                    }}                                            
                                />
                                :
                                <OwnerEntity 
                                    path='ownerPrimary'
                                    detailData={ props.editOwner ? editDetailData.ownerPrimary: null}
                                    onOwnerIndividualChange={(values) => {
                                        fieldActions.onChange(values, 'ownerPrimary');
                                    }}  
                                />
                            }
                        </div>
                        
                        <div className="col-span-6">
                            {(state.values.ownerPrimaryType.value) == 'Individual' ?
                                <FormSelect
                                    label="Vesting*"
                                    type='select'
                                    isSearchable
                                    required
                                    options={state.values.relationshipOptions}
                                    isClearable={false}
                                    groupClassName="col-span-4  mb-6"
                                    className="w-full border-1 border-gray-200 rounded bg-gray-50"
                                    path='relationship'   
                                    value={state.values.relationship}
                                    error={state.errors.relationship}
                                    {...fieldActions}
                                    onChange={(relationship) => {
                                        fieldActions.onChange(relationship, 'relationship');
                                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                    }}                             
                                />
                                : <></>
                            }
                        </div>
                        {
                            (state.values.ownerPrimaryType.value) == 'Individual' && state.values.relationship && ( state.values.relationship.value.includes('and') || state.values.relationship.value.includes('husband') ||
                            state.values.relationship.value.includes('wife')  || state.values.relationship.value.includes('married')  ||   
                            state.values.relationship.value.includes('both')  || state.values.relationship.value.includes('partners'))
                                ?
                                <>
                                    <FormSelect
                                        label="Owner Type*"
                                        type='select'
                                        required
                                        options={state.values.ownerTypeOptions}
                                        isClearable={false}
                                        groupClassName="col-span-1"
                                        className="w-full border-1 border-gray-200 rounded bg-gray-50"
                                        path='ownerSecondaryType'   
                                        value={state.values.ownerSecondaryType}
                                        error={state.errors.ownerSecondaryType}
                                        {...fieldActions}
                                        onChange={(ownerSecondaryType) => {
                                            fieldActions.onChange(ownerSecondaryType, 'ownerSecondaryType');
                                            fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');  
                                        }}                             
                                    />
                                    <div className="col-span-5">
                                        {(state.values.ownerSecondaryType.value) == 'Individual' ?
                                            <OwnerIndividual 
                                                path='ownerSecondary'
                                                detailData={ props.editOwner ? editDetailData.ownerSecondary: null}
                                                onOwnerIndividualChange={(values) => {
                                                    fieldActions.onChange(values, 'ownerSecondary');
                                                }}
                                            />
                                            :
                                            <OwnerEntity 
                                                path='ownerSecondary'
                                                detailData={  props.editOwner ? editDetailData.ownerSecondary: null}
                                                onOwnerIndividualChange={(values) => {
                                                    fieldActions.onChange(values, 'ownerSecondary');
                                                }}  
                                            />
                                        }
                                    </div>
                                </>
                                :
                                <div className="col-span-6"></div>
                        }
                        
                        <div className="col-span-6 flex justify-end items-center mr-4">                                        
                            <FormFooter
                                
                                cancelBtnTxt={'Cancel'}
                                submitBtnTxt={
                                    props.editOwner ? 'Save' : 'Add'}
                                onSubmit={() => {
                                    props.editOwner ? 
                                        props.saveOwner(props.ownerDetailData, state.values)
                                        :
                                        props.addOwner(state.values);
                                }}
                                onCancel={props.onCancel}
                            />                                    
                        </div>
                    </div>
                </div>                    
            </section>     
        </Modal>
    );
};

export default AddOwner;

